
/**
 * Module dependencies.
 */

import {
  SlipResistance,
  slipResistance,
  slipResistanceKeys
} from 'src/core/collections-config/slip-resistance';

import { availableFiltersKeys } from 'src/core/collections-config/filters';
import { units } from 'src/styles/utils';
import { useFiltersList } from 'src/context/filters-list/context';
import { useTranslate } from 'src/core/utils/translator';
import FilterDisclosure from 'src/components/filters/filter-disclosure';
import FilterItem from 'src/components/filters/filter-item';
import FilterItemLabel from 'src/components/filters/filter-item-label';
import React, { ReactElement } from 'react';
import Type from 'src/components/core/typography/index';
import reduce from 'lodash/reduce';
import styled from 'styled-components';
import values from 'lodash/values';

/**
 * Filter name.
 */

const filterName = availableFiltersKeys.slipResistance;

/**
 * `StyledFilterDisclosure` styled component.
 */

const StyledFilterDisclosure = styled(FilterDisclosure)`
  .filters-disclosure-tags {
    text-transform: uppercase;
  }
`;

/**
 * `Group` styled component.
 */

const Group = styled.div`
  padding: ${units(2)} 0 ${units(4)} ${units(3.5)};
`;

/**
 * `GroupTitle` styled component.
 */

const GroupTitle = styled(Type.Paragraph).attrs({ as: 'div' })`
  margin-bottom: ${units(3)};
`;

/**
 * `StyledFilterItemLabel` styled component.
 */

const StyledFilterItemLabel = styled(FilterItemLabel)`
  text-transform: uppercase;
`;

/**
 * `SlipResistanceFilter` component.
 */

const SlipResistanceFilter = (): ReactElement => {
  const { translate } = useTranslate();
  const { filters } = useFiltersList();
  const selectedFilters = reduce(filters, (result, value, key) => {
    if (!values(slipResistanceKeys).includes(key)) {
      return;
    }

    return {
      ...result,
      [key]: value
    };
  }, []);

  return (
    <StyledFilterDisclosure
      name={filterName}
      selectedFilters={values(selectedFilters)}
      title={translate('collections:labels.slipResistance')}
    >
      {slipResistance.map(({ icon, key, values }: SlipResistance) => (
        <Group key={key}>
          <GroupTitle>
            {translate(`collections:slipResistance.${key}`)}
          </GroupTitle>

          {values.map((value: string) => (
            <FilterItem
              key={value}
              label={(
                <StyledFilterItemLabel
                  title={value.replace('_', ',')}
                  type={'icon'}
                  value={icon}
                />
              )}
              name={slipResistanceKeys[key]}
              value={value}
            />
          ))}
        </Group>
      ))}
    </StyledFilterDisclosure>
  );
};

/**
 * Export `SlipResistanceFilter` component.
 */

export default SlipResistanceFilter;
